import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { Link } from "wouter";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ListOrdered,
  Plus,
  Search,
  Pencil,
  Trash2,
  Play,
  Pause,
  Clock,
  Users,
  MessageSquare,
  Zap,
  ChevronRight,
  ArrowRight,
  Settings,
} from "lucide-react";

const TRIGGER_LABELS: Record<string, string> = {
  manual: "Adição manual",
  tag_added: "Tag adicionada",
  new_contact: "Novo contato",
};

export default function Sequences() {
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({
    name: "",
    description: "",
    triggerType: "manual" as "manual" | "tag_added" | "new_contact",
    triggerTagId: undefined as number | undefined,
  });

  const utils = trpc.useUtils();
  const { data: sequences = [], isLoading } = trpc.sequences.list.useQuery();
  const { data: tags = [] } = trpc.tags.list.useQuery();
  const createMutation = trpc.sequences.create.useMutation({
    onSuccess: (seq) => {
      utils.sequences.list.invalidate();
      setCreateOpen(false);
      setForm({ name: "", description: "", triggerType: "manual", triggerTagId: undefined });
      if (seq) setEditingId(seq.id);
    },
  });
  const updateMutation = trpc.sequences.update.useMutation({
    onSuccess: () => utils.sequences.list.invalidate(),
  });
  const deleteMutation = trpc.sequences.delete.useMutation({
    onSuccess: () => { utils.sequences.list.invalidate(); setDeleteId(null); },
  });

  const filtered = sequences.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    (s.description ?? "").toLowerCase().includes(search.toLowerCase())
  );

  const toggleStatus = (seq: typeof sequences[0]) => {
    updateMutation.mutate({ id: seq.id, status: seq.status === "active" ? "inactive" : "active" });
  };

  return (
    <PermissionGuard permission="whatsapp:manage_sequences">
    <DashboardLayout>
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <ListOrdered className="w-6 h-6 text-primary" />
            Sequências
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Envie séries de mensagens automáticas com intervalos de tempo
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="gap-2">
          <Plus className="w-4 h-4" />
          Nova Sequência
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[
          { label: "Total", value: sequences.length, icon: ListOrdered, color: "text-blue-500" },
          { label: "Ativas", value: sequences.filter(s => s.status === "active").length, icon: Play, color: "text-green-500" },
          { label: "Inativas", value: sequences.filter(s => s.status === "inactive").length, icon: Pause, color: "text-gray-400" },
        ].map(stat => (
          <Card key={stat.label} className="border-border/50">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <stat.icon className={`w-8 h-8 ${stat.color}`} />
                <div>
                  <p className="text-2xl font-bold">{stat.value}</p>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Buscar sequências..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* List */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="pt-6 h-40 bg-muted/20 rounded-lg" />
            </Card>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed border-2 border-border/50">
          <CardContent className="flex flex-col items-center justify-center py-16 gap-4">
            <ListOrdered className="w-12 h-12 text-muted-foreground/40" />
            <div className="text-center">
              <p className="font-medium text-muted-foreground">Nenhuma sequência encontrada</p>
              <p className="text-sm text-muted-foreground/60 mt-1">
                {search ? "Tente outro termo" : "Crie sua primeira sequência de mensagens automáticas"}
              </p>
            </div>
            {!search && (
              <Button onClick={() => setCreateOpen(true)} variant="outline" className="gap-2">
                <Plus className="w-4 h-4" />
                Criar primeira sequência
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map(seq => (
            <Card key={seq.id} className="border-border/50 hover:border-primary/30 transition-colors">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-base truncate">{seq.name}</CardTitle>
                    {seq.description && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{seq.description}</p>
                    )}
                  </div>
                  <Badge
                    variant="outline"
                    className={seq.status === "active" ? "bg-green-100 text-green-800 border-green-200" : "bg-gray-100 text-gray-600 border-gray-200"}
                  >
                    {seq.status === "active" ? "Ativa" : "Inativa"}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4">
                  <Zap className="w-3.5 h-3.5 text-yellow-500" />
                  <span>Gatilho: <strong>{TRIGGER_LABELS[seq.triggerType ?? "manual"]}</strong></span>
                </div>
                <div className="flex items-center gap-2">
                  <Link href={`/sequences/${seq.id}`} className="flex-1">
                    <Button variant="outline" size="sm" className="w-full gap-1.5">
                      <Settings className="w-3.5 h-3.5" />
                      Configurar Passos
                    </Button>
                  </Link>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleStatus(seq)}
                    className={seq.status === "active" ? "text-orange-600 hover:text-orange-700" : "text-green-600 hover:text-green-700"}
                  >
                    {seq.status === "active" ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDeleteId(seq.id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="w-[95vw] max-w-md mx-2 sm:mx-0">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListOrdered className="w-5 h-5 text-primary" />
              Nova Sequência
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label>Nome *</Label>
              <Input
                placeholder="Ex: Onboarding, Pós-venda, Reengajamento..."
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Descrição</Label>
              <Textarea
                placeholder="Descreva o objetivo desta sequência..."
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                rows={2}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Gatilho de Entrada</Label>
              <Select value={form.triggerType} onValueChange={v => setForm(f => ({ ...f, triggerType: v as any }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Adição manual</SelectItem>
                  <SelectItem value="tag_added">Quando tag for adicionada</SelectItem>
                  <SelectItem value="new_contact">Novo contato criado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.triggerType === "tag_added" && (
              <div className="flex flex-col gap-1.5">
                <Label>Tag Gatilho</Label>
                <Select
                  value={form.triggerTagId?.toString() ?? ""}
                  onValueChange={v => setForm(f => ({ ...f, triggerTagId: Number(v) }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a tag..." />
                  </SelectTrigger>
                  <SelectContent>
                    {tags.map(tag => (
                      <SelectItem key={tag.id} value={tag.id.toString()}>{tag.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button
              onClick={() => createMutation.mutate(form)}
              disabled={!form.name.trim() || createMutation.isPending}
            >
              {createMutation.isPending ? "Criando..." : "Criar e Configurar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteId !== null} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Sequência</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. A sequência, seus passos e todos os contatos inscritos serão removidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteId && deleteMutation.mutate({ id: deleteId })}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </DashboardLayout>
    </PermissionGuard>
  );
}
